import { checkIfSubjectIsSelected, getSelectedSubjectsTab } from '../utils';
import { OnlineAnalytics } from '~/Partials/GoogleTagManager/Analytics/OnlineAnalytics';
import type { PlannerAction, PlannerState, SelectedSubject } from './types';

export const plannerReducer = (state: PlannerState, action: PlannerAction): PlannerState => {
    switch (action.type) {
        case 'ADD_SELECTED_SUBJECT': {
            const { internalSelectedSubjects, currentBookmark } = state;
            const { subject, semester } = action;

            const newSelectedSubjects = new Map(internalSelectedSubjects);
            const activeTab = currentBookmark.contextKey;

            const isSelected = checkIfSubjectIsSelected(subject, activeTab, semester, internalSelectedSubjects);

            if (isSelected || !subject.canBePurchased) {
                //todo: set error here along with SET_ERROR case in this reducer.
                return state;
            }

            const selectedSubjectsTab = getSelectedSubjectsTab(internalSelectedSubjects);
            const isSelectionFromDifferentTab = selectedSubjectsTab && selectedSubjectsTab !== activeTab;

            if (isSelectionFromDifferentTab) {
                return {
                    ...state,
                    isPopupVisible: true,
                };
            }

            const selectedSubject = { ...subject, semester, tab: activeTab };

            newSelectedSubjects.set(subject.subjectCode, selectedSubject);
            OnlineAnalytics.addOnlinePlannerProducts([selectedSubject]);

            return {
                ...state,
                internalSelectedSubjects: newSelectedSubjects,
            };
        }

        case 'ADD_BATCH_SELECTED_SUBJECTS': {
            const { internalSelectedSubjects, currentBookmark } = state;
            const { subjects, semester } = action;

            const activeTab = currentBookmark.contextKey;
            const selectedSubjectsTab = getSelectedSubjectsTab(internalSelectedSubjects);

            const isSelectionFromDifferentTab = selectedSubjectsTab && selectedSubjectsTab !== activeTab;

            if (isSelectionFromDifferentTab) {
                return {
                    ...state,
                    isPopupVisible: true,
                };
            }

            const newSelectedSubjects = new Map(internalSelectedSubjects);
            const addedSubjects: SelectedSubject[] = [];

            for (const validSubject of subjects.filter(subject => subject.canBePurchased)) {
                const selectedSubject = { ...validSubject, semester, tab: activeTab };

                if (newSelectedSubjects.set(validSubject.subjectCode, selectedSubject)) {
                    addedSubjects.push(selectedSubject);
                }
            }

            OnlineAnalytics.addOnlinePlannerProducts(addedSubjects);

            return {
                ...state,
                internalSelectedSubjects: newSelectedSubjects,
            };
        }

        case 'SET_CURRENT_BOOKMARK': {
            return {
                ...state,
                currentBookmark: action.bookmark,
            };
        }

        case 'SET_CURRENT_FILTER': {
            return {
                ...state,
                currentFilter: action.filter,
            };
        }

        case 'REMOVE_SELECTED_SUBJECT': {
            const { internalSelectedSubjects } = state;
            const { subject } = action;

            const selectedSubject = internalSelectedSubjects.get(subject.subjectCode);

            if (!selectedSubject) {
                return state;
            }

            const newSelectedSubjects = new Map(internalSelectedSubjects);

            newSelectedSubjects.delete(selectedSubject.subjectCode);
            OnlineAnalytics.removeOnlinePlannerProducts([selectedSubject]);

            return {
                ...state,
                internalSelectedSubjects: newSelectedSubjects,
            };
        }

        case 'REMOVE_BATCH_SELECTED_SUBJECTS': {
            const { internalSelectedSubjects, currentBookmark } = state;
            const { semester } = action;

            const activeTab = currentBookmark.contextKey;
            const selectedSubjectsTab = getSelectedSubjectsTab(internalSelectedSubjects);

            const isSelectionFromDifferentTab = selectedSubjectsTab && selectedSubjectsTab !== activeTab;

            if (isSelectionFromDifferentTab) {
                return {
                    ...state,
                    isPopupVisible: true,
                };
            }

            const newSelectedSubjects = new Map(internalSelectedSubjects);
            const removedSubjects = Array.from(internalSelectedSubjects.values()).filter(
                subject => subject.semester === semester
            );

            for (const removedSubject of removedSubjects) {
                newSelectedSubjects.delete(removedSubject.subjectCode);
            }

            OnlineAnalytics.removeOnlinePlannerProducts(removedSubjects);

            return {
                ...state,
                internalSelectedSubjects: newSelectedSubjects,
            };
        }

        case 'REMOVE_ALL_SELECTED_SUBJECTS': {
            const { internalSelectedSubjects } = state;

            if (internalSelectedSubjects.size !== 0) {
                OnlineAnalytics.removeOnlinePlannerProducts(Array.from(internalSelectedSubjects.values()));
            }

            return {
                ...state,
                internalSelectedSubjects: new Map(),
            };
        }

        case 'SET_POPUP_VISIBILITY': {
            return {
                ...state,
                isPopupVisible: action.open,
            };
        }

        case 'SET_ERROR': {
            //todo: Implement error reducer state.
            return state;
        }

        default:
            return state;
    }
};
